import React, { useEffect, useState } from "react"
import { Popover } from "@mantine/core"
import { useQueryClient } from "@tanstack/react-query"

import {
    CUSTOM_REGIONS_QUERY_INVALIDATE_EVENT,
    CUSTOM_REGIONS_QUERY_KEY,
} from "@/components/TopNavigation/components/RegionSelector/constants"
import { Target } from "@/components/TopNavigation/components/RegionSelector/Target/Target"
import { Dropdown } from "@/components/TopNavigation/components/RegionSelector/Dropdown/Dropdown"
import { getCurrentRegionValue, getRegionGroups } from "@/components/TopNavigation/components/RegionSelector/helpers"
import { useStyles } from "@/components/TopNavigation/components/RegionSelector/RegionSelector.styles"
import { RegionSelectorContext } from "@/components/TopNavigation/components/RegionSelector/RegionSelectorContext"
import { useCustomRegions, useRecentRegions } from "@/components/TopNavigation/components/RegionSelector/services"

type RegionSelectorProps = {
    currentRegionName: string
    loggedInAsUser: boolean
    popdownIsOpen: boolean
}

export const RegionSelector = ({ currentRegionName, loggedInAsUser, popdownIsOpen }: RegionSelectorProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const [selectedRegionValue, setSelectedRegionValue] = useState(getCurrentRegionValue())
    const customRegions = useCustomRegions()
    const recentRegions = useRecentRegions()
    const queryClient = useQueryClient()
    const { classes } = useStyles()

    const toggleIsOpen = () => setIsOpen((wasOpen) => !wasOpen)

    const regionGroups = getRegionGroups()

    useEffect(() => {
        const invalidateQuery = () => {
            queryClient.invalidateQueries({ queryKey: [CUSTOM_REGIONS_QUERY_KEY] })
        }

        window.addEventListener(CUSTOM_REGIONS_QUERY_INVALIDATE_EVENT, invalidateQuery)

        return () => window.removeEventListener(CUSTOM_REGIONS_QUERY_INVALIDATE_EVENT, invalidateQuery)
    }, [queryClient])

    return (
        <RegionSelectorContext.Provider
            value={{
                customRegions,
                recentRegions,
                regionGroups,
                selectedRegionValue,
                setSelectedRegionValue,
                toggleIsOpen,
            }}
        >
            <Popover opened={isOpen} onChange={setIsOpen} offset={4} position="bottom-start" width="25rem" trapFocus>
                <Popover.Target>
                    <Target
                        currentRegionName={currentRegionName}
                        isOpen={isOpen}
                        loggedInAsUser={loggedInAsUser}
                        popdownIsOpen={popdownIsOpen}
                        onClick={toggleIsOpen}
                    />
                </Popover.Target>

                <Popover.Dropdown className={classes.dropdown}>
                    <Dropdown onCancel={toggleIsOpen} />
                </Popover.Dropdown>
            </Popover>
        </RegionSelectorContext.Provider>
    )
}
